import React from 'react';
import { IAuditoriaQualidadeHistoricosResultado } from '../interfaces/IAuditoriaQualidadeHistoricos';
import { TributoAuditoria } from '../interfaces/IAuditoria';

interface Props {
  resultado?: IAuditoriaQualidadeHistoricosResultado;
  onSelecionar: (tributo: TributoAuditoria) => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// COR-219: rótulo legível — evita mostrar "REFERENCIAL_DIVERGENTE" cru (nome interno de status) na tela.
const getRotuloStatus = (status: string) => (status === 'REFERENCIAL_DIVERGENTE' ? 'Referencial Divergente' : status);

const AuditoriaResumoQualidadeHistoricos: React.FC<Props> = ({ resultado, onSelecionar }) => {
  if (!resultado) return null;

  const totalContas = resultado.contas?.length || 0;
  const contasInelegiveis = resultado.contas?.filter(c => c.elegibilidadeConciliacao === 'INELEGIVEL').length || 0;

  return (
    <div className='auditoria-card mt-5'>
      <h3>Resumo de Auditoria de Qualidade dos Históricos — Fornecedores e Clientes</h3>
      <p className='text-muted'>
        Validação semântica do campo Histórico nos lançamentos de Fornecedores (2.01.01.03.01) e Clientes
        (1.01.02.02.01). A ausência simultânea de Nota Fiscal e Razão Social qualifica o histórico como
        desqualificado. Índice abaixo de 90% classifica a conta como Risco de Auditoria e a empresa como
        Inelegível para Conciliação Automática. Clique para ver a análise detalhada.
      </p>

      <div className='table-responsive mt-4'>
        <table className='auditoria-table auditoria-table-tributos'>
          <thead>
            <tr>
              <th>Auditoria</th>
              <th className='text-end'>Multa ECD Potencial</th>
              <th className='text-center'>Contas Analisadas</th>
              <th className='text-center'>Inelegíveis</th>
              <th className='text-center'>Erros</th>
              <th className='text-center'>Alertas</th>
              <th className='text-center' title='Evidência parcial (nome identificável, ajuste/reclassificação ou liquidação sem vínculo confirmado) — sem multa automática'>Pendentes</th>
              <th className='text-center' title='Descrição na ECD sugere instituição financeira mapeada como Fornecedores/Clientes — indício complementar, não suspende a análise'>Indício Cadastral</th>
            </tr>
          </thead>
          <tbody>
            <tr className='auditoria-row-tributo' onClick={() => onSelecionar('AUDITORIA_QUALIDADE_HISTORICOS')}>
              <td className='auditoria-col-tributo'>
                <div className='d-flex align-items-center gap-2'>
                  <strong>Qualidade dos Históricos — Fornecedores e Clientes</strong>
                  <span className={`auditoria-status status-${resultado.status}`}>
                    {getRotuloStatus(resultado.status)}
                  </span>
                </div>
                <div className='auditoria-legislacao-inline'>
                  <div>
                    {totalContas} conta{totalContas === 1 ? '' : 's'} de fornecedores/clientes analisada{totalContas === 1 ? '' : 's'} no período
                  </div>
                  <div className='text-muted'>{resultado.mensagemDirecionamento}</div>
                </div>
              </td>
              <td className='text-end auditoria-col-highlight'>
                {formatCurrency(resultado.impactoFiscalTotal)}
                <div className='auditoria-kpi-help'>
                  Multa ECD potencial (5% limitada a 1% da receita bruta do período) para lançamentos de
                  constituição de obrigação/receita sem NF e sem Razão Social — sujeita a confirmação
                  (art. 12, II, Lei nº 8.218/1991)
                </div>
              </td>
              <td className='text-center'>
                <span className='auditoria-contador'>{totalContas}</span>
              </td>
              <td className='text-center'>
                <span className={`auditoria-contador ${contasInelegiveis > 0 ? 'auditoria-contador-erro' : ''}`}>
                  {contasInelegiveis}
                </span>
              </td>
              <td className='text-center'>
                <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>
              </td>
              <td className='text-center'>
                <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>
              </td>
              <td className='text-center'>
                <span className='auditoria-contador auditoria-contador-pendente'>{resultado.totalPendentes}</span>
              </td>
              <td className='text-center'>
                <span className='auditoria-contador auditoria-contador-secundario'>{resultado.totalReferencialDivergente}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditoriaResumoQualidadeHistoricos;
